import { FC, useState } from 'react';
import {
	Card,
	Collapse,
	Empty,
	Table,
	TableColumnType,
	Upload,
	Button,
	Divider,
} from 'antd';
import {
	LoadingOutlined,
	UploadOutlined,
	FileSearchOutlined,
} from '@ant-design/icons';
import { useModals } from '@/components/modals/ModalProvider';
import {
	monitoringDocumentGetSignedUrl,
	monitoringDocumentUploadFile,
	MonitoringDocumentSection,
	MonitoringDocumentSubCategory,
} from '@/libs/fleet-api/services/monitoring-document';
import dayjs from 'dayjs';
import { addWatermark } from '@/utils/file-transform';
import { useUserData } from '@/libs/fleet-api/hooks';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import { message } from 'antd';

const MonitoringDocumentDocumentDetail: FC<{
	keyword: string;
	data: MonitoringDocumentSubCategory;
	fetchType: string;
	categoryId: string;
	onDownloadFinish: () => void;
	customerId: string;
	onSpfSelected?: (spfNo: string, spfStatus: string) => void;
	onMppSelected?: (mppNo: string, agreementNo: string) => void;
}> = ({
	data,
	keyword,
	fetchType,
	categoryId,
	onDownloadFinish,
	customerId,
	onSpfSelected,
	onMppSelected,
}) => {
	const [activeKey, setActiveKey] = useState<string[]>([]);
	const [selectedSpfSectionId, setSelectedSpfSectionId] = useState<
		string | null
	>(null);
	// State for MPP selection (for financing-disbursement-document)
	const [selectedMppSectionId, setSelectedMppSectionId] = useState<
		string | null
	>(null);
	// State for Disbursement selection (for financing-disbursement-document)
	const [selectedDisbursementSectionId, setSelectedDisbursementSectionId] =
		useState<string | null>(null);

	const [isLoading, setIsLoading] = useState<string | null>(null);
	const { openPreviewModal } = useModals();
	const userData = useUserData();

	// Category Checks
	const isFinancingDisbursementDocument =
		categoryId.toLowerCase() === 'financing-disbursement-document';
	const isCovenantDocument = categoryId.toLowerCase() === 'covenant-document';

	// Determine if we should show the disbursement details view
	const isFinancingDisbursementWithSelectedDisbursement =
		isFinancingDisbursementDocument &&
		selectedMppSectionId &&
		selectedDisbursementSectionId;

	const allSectionsAreSpf =
		isCovenantDocument &&
		data.sections &&
		data.sections.length > 0 &&
		data.sections.every((section) => section.id.startsWith('spf-'));

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	// fetchType is available for conditional UI rendering based on fetch type
	// Currently not used but available for future implementation
	void fetchType;

	// Extract SPF number and status from section label (format: "spfNo|spfStatus")
	const extractSpfInfo = (label: string) => {
		const parts = label.split('|');
		return {
			spfNo: parts[0] || label,
			spfStatus: parts[1] || '',
		};
	};

	// Extract MPP number and agreement number from section label (format: "mppNo|agreementNo")
	const extractMppInfo = (label: string) => {
		const parts = label.split('|');
		return {
			mppNo: parts[0] || label,
			agreementNo: parts[1] || '',
		};
	};

	const renderSection = ({
		documents,
		id,
		label,
	}: MonitoringDocumentSection) => {
		if (!documents || !Array.isArray(documents)) {
			return <Empty description="No documents available in this section" />;
		}

		const dataSource = documents.map((record, index) => {
			const no = index + 1;
			const name = record.name;
			const type = record.documentType;
			const description = record.description;

			// Take the first file if multiple files exist
			const file =
				record.files && record.files.length > 0 ? record.files[0] : undefined;

			return {
				key: index.toString(),
				no,
				name,
				type,
				file,
				documentType: record.documentType,
				refDocumentId: record.refDocumentId,
				uploadBy: record.uploadBy,
				description,
				allowUpload: record.allowUpload,
				prospectId: record.prospectId,
			};
		});

		const getTitleNameById = (id: string) => {
			switch (id) {
				case 'shareholder':
					return 'Nama Pemegang Saham';
				case 'commissioner-identity':
					return 'Nama Komisaris';
				case 'director-identity':
					return 'Nama Direktur';
				default:
					return 'Nama';
			}
		};

		// Check if there is any "Komisaris" in the shareholder list
		// The shareholder section has id 'shareholder'
		const shareholderSection = data.sections.find(
			(s) => s.id === 'shareholder',
		);
		const hasKomisaris = shareholderSection?.documents?.some((doc) => {
			// Check name or description for "Komisaris" (case insensitive)
			// Assuming the position/role might be in the name or description
			const nameHasKomisaris = doc.name?.toLowerCase().includes('komisaris');
			const descHasKomisaris = doc.description
				?.toLowerCase()
				.includes('komisaris');
			return nameHasKomisaris || descHasKomisaris;
		});

		const results = keyword
			? dataSource.filter((d) => {
					if (
						id === 'shareholder' ||
						id === 'commissioner-identity' ||
						id === 'director-identity'
					) {
						return (
							d.file?.documentNo
								?.toLowerCase()
								?.includes(keyword.toLowerCase()) ||
							d.name?.toLowerCase()?.includes(keyword.toLowerCase()) ||
							d.file?.fileName?.toLowerCase()?.includes(keyword.toLowerCase())
						);
					}

					return (
						d.name?.toLowerCase().includes(keyword.toLowerCase()) ||
						d.file?.fileName?.toLowerCase()?.includes(keyword.toLowerCase())
					);
				})
			: dataSource;

		if (!results.length) {
			return (
				<Empty
					description={
						keyword ? (
							<>
								Your search <b>{`"${keyword}"`}</b> did not match any documents
								in <b>{label}</b>.
							</>
						) : (
							'No Data'
						)
					}
				/>
			);
		}

		type DataSourceType = (typeof dataSource)[number];
		const columns: TableColumnType<DataSourceType>[] = [
			{
				title: 'No',
				align: 'center',
				render: (_, { no }) => no,
			},
			...(id === 'shareholder' ||
			id === 'commissioner-identity' ||
			id === 'director-identity'
				? [
						{
							title: 'NIK',
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record.description || record.file?.documentNo,
						},
						{
							title: getTitleNameById(id),
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record?.name,
						},
					]
				: [
						{
							title: 'Nama File',
							width: 300,
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record.name,
						},
					]),
			{
				title: 'Upload Dokumen',
				align: 'center',
				width: 100,
				render: (_: unknown, record: (typeof dataSource)[number]) => {
					// Disable upload for commissioner-identity if no Komisaris in shareholder list
					const isCommissionerIdentity = id === 'commissioner-identity';
					const isDisabledCallback = isCommissionerIdentity && !hasKomisaris;
					const disabled = !record.allowUpload || isDisabledCallback;

					return (
						<Upload
							multiple={false}
							disabled={disabled}
							showUploadList={false}
							beforeUpload={(file) => {
								const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
								const filepath = `customer/${customerId}/monitoring/document/${record.name}-${fileId}`;

								uploadMutation
									.mutateAsync({
										file,
										fileid: fileId,
										filepath,
									})
									.then(async () => {
										try {
											await monitoringDocumentUploadFile({
												customerId,
												prospectId: record.prospectId || data.prospectId,
												generalId: record.refDocumentId ?? null,
												file: {
													id: fileId,
													name: file.name,
													path: filepath,
												},
												slug: record.documentType,
											});
											message.success('File berhasil diupload');
											onDownloadFinish();
										} catch (error) {
											message.error(
												'Gagal update status upload: ' +
													(error as Error).message,
											);
										}
									})
									.catch((error) => {
										message.error('Upload gagal: ' + error.message);
									});

								return false; // Prevent default upload
							}}
						>
							<Button
								icon={<UploadOutlined />}
								type="link"
								disabled={disabled}
							/>
						</Upload>
					);
				},
			},
			...(id === 'section-company-legality' ||
			id === 'section-business-legality'
				? [
						{
							title: 'No Dokumen',
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record.file?.documentNo,
						},
						{
							title: 'Tanggal Upload',
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record.file?.uploadDate &&
								dayjs(record.file.uploadDate).format('DD MMM YYYY'),
						},
					]
				: [
						{
							title: 'Tanggal Upload',
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record.file?.uploadDate &&
								dayjs(record.file.uploadDate).format('DD MMM YYYY'),
						},
					]),
			{
				title: 'Upload By',
				align: 'center',
				render: (_: unknown, record: (typeof dataSource)[number]) =>
					record.file?.uploadBy ||
					record.file?.uploadedBy ||
					record.uploadBy ||
					'-',
			},
			{
				title: 'View Dokumen',
				align: 'center',
				render: (_: unknown, record: (typeof dataSource)[number]) => {
					const uniqueLoadingId = `${id}-${record.documentType}-${record.file?.fileId}`;

					if (isLoading === uniqueLoadingId) {
						return (
							<LoadingOutlined
								spin
								style={{ color: '#1557BC' }}
							/>
						);
					}

					return (
						<Button
							type="link"
							icon={<FileSearchOutlined />}
							disabled={!record.file?.fileId}
							onClick={async (e) => {
								e.preventDefault();
								if (!record.file?.fileId || isLoading === uniqueLoadingId)
									return;

								setIsLoading(uniqueLoadingId);

								try {
									const {
										data: { signedUrl },
									} = await monitoringDocumentGetSignedUrl(record.file!.fileId);

									const getDocumentType = (name: string) => {
										if (name.endsWith('.pdf')) return 'pdf';
										if (name.endsWith('.jpeg')) return 'jpeg';
										if (name.endsWith('.jpg')) return 'jpg';
										if (name.endsWith('.png')) return 'png';
										return undefined;
									};

									const { fileId, fileName } = record.file ?? {};
									const fileType = getDocumentType(record.file!.fileName);
									// if file type is not supported, open the original file
									if (!fileType) {
										openPreviewModal({
											id: String(fileId),
											name: fileName!,
											format: getDocumentType(fileName!),
											url: signedUrl,
										});
										return;
									}

									// Add watermark to the file
									const watermarkedBlob = await addWatermark(
										signedUrl,
										fileType!,
										{
											text: `${userData?.fullName}\n${dayjs().format('DD/MM/YYYY - HH:mm:ss')}`,
											opacity: 0.2,
											fontSize:
												getDocumentType(fileName!) === 'pdf' ? 280 : 160,
											color: '#111111',
										},
									);
									const watermarkedUrl = URL.createObjectURL(watermarkedBlob);

									openPreviewModal({
										id: String(fileId),
										name: fileName!,
										format: getDocumentType(fileName!),
										url: watermarkedUrl,
									});
									// Clean up the object URL when the modal is closed
									setTimeout(() => {
										URL.revokeObjectURL(watermarkedUrl);
									}, 1000);
								} finally {
									setIsLoading(null);
								}
							}}
						/>
					);
				},
			},
		];

		return (
			<Table
				bordered
				pagination={false}
				scroll={{ x: 'max-content' }}
				dataSource={results}
				columns={columns}
			/>
		);
	};

	if (!data.sections || !Array.isArray(data.sections)) {
		return <Empty description="No sections available" />;
	}

	const renderFinancingDisbursementView = () => {
		const mppId = selectedMppSectionId!.replace('mpp-select-', '');
		const disbursementId = selectedDisbursementSectionId!.replace(
			'disbursement-select-',
			'',
		);

		// Get main disbursement section
		const mainSection = data.sections.find(
			(s) => s.id === `mpp-${mppId}-disbursement-${disbursementId}`,
		);

		// Get all related sections (PO and Agreements)
		// We can filter by ID prefix since we used a consistent naming convention
		const relatedSections = data.sections.filter(
			(s) =>
				s.id.startsWith(`mpp-${mppId}-disbursement-${disbursementId}-po-`) ||
				s.id.startsWith(
					`mpp-${mppId}-disbursement-${disbursementId}-agreement-`,
				),
		);

		const poSections = relatedSections.filter((s) => s.metadata?.type === 'po');
		const agreementsByPo = relatedSections
			.filter((s) => s.metadata?.type === 'agreement')
			.reduce(
				(acc, agreement) => {
					const poId = agreement.metadata?.poId;
					if (poId) {
						if (!acc[poId]) acc[poId] = [];
						acc[poId].push(agreement);
					}
					return acc;
				},
				{} as Record<number, MonitoringDocumentSection[]>,
			);

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
				{/* Skema Section */}
				{mainSection && (
					<Collapse
						defaultActiveKey={['skema']}
						items={[
							{
								key: 'skema',
								label: 'Skema',
								children: (
									<div>
										<div style={{ marginBottom: 16 }}>
											Skema Pembiayaan: {mainSection.metadata?.scheme || '-'}
										</div>
										<Divider />
										<h4 style={{ marginBottom: 16 }}>List Dokumen</h4>
										{renderSection(mainSection)}
									</div>
								),
							},
						]}
					/>
				)}

				{/* Purchase Order List Section */}
				{poSections.length > 0 && (
					<div>
						<h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 600 }}>
							List Purchase Order
						</h3>
						<Collapse
							defaultActiveKey={[]}
							items={poSections.map((poSection) => {
								const poId = poSection.metadata?.poId;
								const relatedAgreements = poId
									? agreementsByPo[poId] || []
									: [];

								return {
									key: poSection.id,
									label: poSection.label,
									children: (
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												gap: 16,
											}}
										>
											{renderSection(poSection)}
											{relatedAgreements.length > 0 && (
												<Collapse
													defaultActiveKey={[]}
													items={relatedAgreements.map((agreement) => {
														// Extract agreement number from label "Agreement {no}"
														const agreementNo = agreement.label.replace(
															'Agreement ',
															'',
														);
														return {
															key: agreement.id,
															label: `No. Agreement (${agreementNo})`,
															style: {
																marginBottom: 8,
																border: 'none',
																borderRadius: 4,
															},
															styles: {
																header: {
																	background: '#FFFDE7',
																	borderRadius: 4,
																},
															},
															children: (
																<div>
																	<h5
																		style={{
																			marginBottom: 8,
																			fontWeight: 600,
																			fontSize: 14,
																			color: '#666',
																		}}
																	>
																		List Dokumen Agreement
																	</h5>
																	{renderSection(agreement)}
																</div>
															),
														};
													})}
												/>
											)}
										</div>
									),
								};
							})}
						/>
					</div>
				)}
			</div>
		);
	};

	if (!data.sections || !Array.isArray(data.sections)) {
		return <Empty description="No sections available" />;
	} else if (
		isFinancingDisbursementDocument &&
		isFinancingDisbursementWithSelectedDisbursement
	) {
		return renderFinancingDisbursementView();
	}

	// Special handling for covenant-document: show SPF selection first

	// Special handling for financing-disbursement-document: show MPP selection first
	const mppSelectionSections = isFinancingDisbursementDocument
		? data.sections.filter((section) => section.id.startsWith('mpp-select-'))
		: [];

	// Determine if we should show the disbursement details view
	// If covenant-document with multiple SPF sections and no SPF selected yet, show SPF selection
	if (allSectionsAreSpf && !selectedSpfSectionId) {
		const filteredSpfSections = keyword
			? data.sections.filter((section) =>
					section.label.toLowerCase().includes(keyword.toLowerCase()),
				)
			: data.sections;

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
				<h3
					style={{
						marginBottom: 0,
						fontSize: 16,
						fontWeight: 600,
						backgroundColor: '#E3F2FD',
						padding: '12px 16px',
						borderRadius: '4px 4px 0 0',
					}}
				>
					Pilih Surat Persetujuan Fasilitas (SPF)
				</h3>
				{filteredSpfSections.length ? (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 12,
						}}
					>
						{filteredSpfSections.map((section) => {
							const { spfNo } = extractSpfInfo(section.label);
							return (
								<Card
									key={section.id}
									style={{
										cursor: 'pointer',
										border: '1px solid #E0E0E0',
										borderRadius: 4,
									}}
									onClick={() => {
										setSelectedSpfSectionId(section.id);
										const { spfNo: selectedSpfNo, spfStatus } = extractSpfInfo(
											section.label,
										);
										if (onSpfSelected) {
											onSpfSelected(selectedSpfNo, spfStatus);
										}
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 12,
										}}
									>
										<svg
											width="32"
											height="32"
											viewBox="0 0 32 32"
											fill="none"
										>
											<path
												d="M26.6641 7.99967H14.6641L11.9974 5.33301H5.33073C3.86406 5.33301 2.66406 6.53301 2.66406 7.99967V13.333H29.3307V10.6663C29.3307 9.19967 28.1307 7.99967 26.6641 7.99967Z"
												fill="#FFA000"
											/>
											<path
												d="M26.6641 8H5.33073C3.86406 8 2.66406 9.2 2.66406 10.6667V24C2.66406 25.4667 3.86406 26.6667 5.33073 26.6667H26.6641C28.1307 26.6667 29.3307 25.4667 29.3307 24V10.6667C29.3307 9.2 28.1307 8 26.6641 8Z"
												fill="#FFCA28"
											/>
										</svg>
										<strong>{spfNo}</strong>
									</div>
								</Card>
							);
						})}
					</div>
				) : (
					<div
						style={{
							backgroundColor: '#fff',
							padding: '16px',
							borderRadius: '0 0 4px 4px',
							border: '1px solid #E0E0E0',
							borderTop: 'none',
						}}
					>
						<Empty
							description={
								keyword ? (
									<>
										Your search <b>{`"${keyword}"`}</b> did not match any SPF.
									</>
								) : (
									'No SPF available'
								)
							}
						/>
					</div>
				)}
			</div>
		);
	}

	// If financing-disbursement-document with MPP selection sections and no MPP selected yet, show MPP selection
	if (
		isFinancingDisbursementDocument &&
		mppSelectionSections.length > 0 &&
		!selectedMppSectionId
	) {
		const filteredMppSections = keyword
			? mppSelectionSections.filter((section) =>
					section.label.toLowerCase().includes(keyword.toLowerCase()),
				)
			: mppSelectionSections;

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
				<h3
					style={{
						marginBottom: 0,
						fontSize: 16,
						fontWeight: 600,
						backgroundColor: '#E3F2FD',
						padding: '12px 16px',
						borderRadius: '4px 4px 0 0',
					}}
				>
					Pilih Memo Pencairan Pembiayaan (MPP)
				</h3>
				{filteredMppSections.length ? (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 12,
						}}
					>
						{filteredMppSections.map((section) => {
							const { mppNo } = extractMppInfo(section.label);
							return (
								<Card
									key={section.id}
									style={{
										cursor: 'pointer',
										border: '1px solid #E0E0E0',
										borderRadius: 4,
									}}
									onClick={() => {
										setSelectedMppSectionId(section.id);
										setSelectedDisbursementSectionId(null);
										const { mppNo: selectedMppNo, agreementNo } =
											extractMppInfo(section.label);
										if (onMppSelected) {
											onMppSelected(selectedMppNo, agreementNo);
										}
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 12,
										}}
									>
										<svg
											width="32"
											height="32"
											viewBox="0 0 32 32"
											fill="none"
										>
											<path
												d="M26.6641 7.99967H14.6641L11.9974 5.33301H5.33073C3.86406 5.33301 2.66406 6.53301 2.66406 7.99967V13.333H29.3307V10.6663C29.3307 9.19967 28.1307 7.99967 26.6641 7.99967Z"
												fill="#FFA000"
											/>
											<path
												d="M26.6641 8H5.33073C3.86406 8 2.66406 9.2 2.66406 10.6667V24C2.66406 25.4667 3.86406 26.6667 5.33073 26.6667H26.6641C28.1307 26.6667 29.3307 25.4667 29.3307 24V10.6667C29.3307 9.2 28.1307 8 26.6641 8Z"
												fill="#FFCA28"
											/>
										</svg>
										<strong>{mppNo}</strong>
									</div>
								</Card>
							);
						})}
					</div>
				) : (
					<div
						style={{
							backgroundColor: '#fff',
							padding: '16px',
							borderRadius: '0 0 4px 4px',
							border: '1px solid #E0E0E0',
							borderTop: 'none',
						}}
					>
						<Empty
							description={
								keyword ? (
									<>
										Your search <b>{`"${keyword}"`}</b> did not match any MPP.
									</>
								) : (
									'No MPP available'
								)
							}
						/>
					</div>
				)}
			</div>
		);
	}

	// Special handling for financing-disbursement-document after MPP is selected
	if (
		isFinancingDisbursementDocument &&
		selectedMppSectionId &&
		!selectedDisbursementSectionId
	) {
		// Extract MPP ID from selected section ID (format: "mpp-select-{mppId}")
		const mppId = selectedMppSectionId.replace('mpp-select-', '');

		// Get MPP documents section
		const mppDocsSection = data.sections.find(
			(section) => section.id === `mpp-docs-${mppId}`,
		);

		// Get disbursement selection sections
		const disbursementSelectionSections = data.sections.filter((section) =>
			section.id.startsWith(`disbursement-select-`),
		);

		// Filter disbursement selection sections that belong to this MPP
		// We need to check if the disbursement belongs to this MPP by checking if there are related sections
		const mppRelatedDisbursementIds = new Set<string>();

		// Find all sections that start with "mpp-{mppId}-"
		data.sections.forEach((section) => {
			if (section.id.startsWith(`mpp-${mppId}-disbursement-`)) {
				// Extract disbursement ID
				// Format: mpp-{mppId}-disbursement-{disbursementId}...
				const parts = section.id.split('-');
				// parts[0] = mpp, parts[1] = mppId, parts[2] = disbursement, parts[3] = disbursementId
				if (parts.length >= 4) {
					mppRelatedDisbursementIds.add(parts[3]);
				}
			}
		});

		const filteredDisbursementSelectionSections =
			disbursementSelectionSections.filter((section) => {
				const disbursementId = section.id.replace('disbursement-select-', '');
				return mppRelatedDisbursementIds.has(disbursementId);
			});

		const filteredSections = keyword
			? filteredDisbursementSelectionSections.filter((section) =>
					section.label.toLowerCase().includes(keyword.toLowerCase()),
				)
			: filteredDisbursementSelectionSections;

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
				{/* MPP Documents Section */}
				{mppDocsSection && (
					<div style={{ marginBottom: 20 }}>
						<h3
							style={{
								marginBottom: 0,
								fontSize: 16,
								fontWeight: 600,
								backgroundColor: '#E3F2FD',
								padding: '12px 16px',
								borderRadius: '4px 4px 0 0',
							}}
						>
							Dokumen Memo Pencairan Pembiayaan (MPP)
						</h3>
						<div
							style={{
								border: '1px solid #f0f0f0',
								borderRadius: '0 0 4px 4px',
								padding: 16,
							}}
						>
							{renderSection(mppDocsSection)}
						</div>
					</div>
				)}

				<h3
					style={{
						marginBottom: 0,
						fontSize: 16,
						fontWeight: 600,
						backgroundColor: '#E3F2FD',
						padding: '12px 16px',
						borderRadius: '4px 4px 0 0',
					}}
				>
					Pilih No. Dokumen Kesepakatan Realisasi Pencairan
				</h3>
				{filteredSections.length ? (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 12,
						}}
					>
						{filteredSections.map((section) => {
							return (
								<Card
									key={section.id}
									style={{
										cursor: 'pointer',
										border: '1px solid #E0E0E0',
										borderRadius: 4,
									}}
									onClick={() => {
										setSelectedDisbursementSectionId(section.id);
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 12,
										}}
									>
										<svg
											width="32"
											height="32"
											viewBox="0 0 32 32"
											fill="none"
										>
											<path
												d="M26.6641 7.99967H14.6641L11.9974 5.33301H5.33073C3.86406 5.33301 2.66406 6.53301 2.66406 7.99967V13.333H29.3307V10.6663C29.3307 9.19967 28.1307 7.99967 26.6641 7.99967Z"
												fill="#FFA000"
											/>
											<path
												d="M26.6641 8H5.33073C3.86406 8 2.66406 9.2 2.66406 10.6667V24C2.66406 25.4667 3.86406 26.6667 5.33073 26.6667H26.6641C28.1307 26.6667 29.3307 25.4667 29.3307 24V10.6667C29.3307 9.2 28.1307 8 26.6641 8Z"
												fill="#FFCA28"
											/>
										</svg>
										<strong>{section.label}</strong>
									</div>
								</Card>
							);
						})}
					</div>
				) : (
					<div
						style={{
							backgroundColor: '#fff',
							padding: '16px',
							borderRadius: '0 0 4px 4px',
							border: '1px solid #E0E0E0',
							borderTop: 'none',
						}}
					>
						<Empty
							description={
								keyword ? (
									<>
										Your search <b>{`"${keyword}"`}</b> did not match any
										Disbursement.
									</>
								) : (
									'No Disbursement available'
								)
							}
						/>
					</div>
				)}
			</div>
		);
	}

	// Filter sections based on selection
	let sections = data.sections;

	if (isCovenantDocument && selectedSpfSectionId) {
		sections = data.sections.filter(
			(section) => section.id === selectedSpfSectionId,
		);
	} else if (isFinancingDisbursementDocument && selectedDisbursementSectionId) {
		// Extract MPP ID and Disbursement ID
		const mppId = selectedMppSectionId!.replace('mpp-select-', '');
		const disbursementId = selectedDisbursementSectionId.replace(
			'disbursement-select-',
			'',
		);

		// Get metadata
		const mppSelectSection = data.sections.find(
			(s) => s.id === selectedMppSectionId,
		);
		const disbursementSelectSection = data.sections.find(
			(s) => s.id === selectedDisbursementSectionId,
		);

		const mppMetadata = mppSelectSection?.metadata || {};
		const disbursementMetadata = disbursementSelectSection?.metadata || {};
		const subCategoryMetadata = data.metadata || {};

		// Show sections related to this disbursement (Disbursement docs, POs, Agreements)
		// These sections have IDs starting with "mpp-{mppId}-disbursement-{disbursementId}"
		sections = data.sections.filter((section) =>
			section.id.startsWith(`mpp-${mppId}-disbursement-${disbursementId}`),
		);

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
				{/* Header Info */}
				<div
					style={{
						padding: 16,
						backgroundColor: '#FFFBE6',
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						gap: 16,
					}}
				>
					{/* Row 1 */}
					<div>
						<div style={{ fontWeight: 600 }}>Nama Customer</div>
						<div>{subCategoryMetadata.customerName || '-'}</div>
					</div>
					<div>
						<div style={{ fontWeight: 600 }}>Group BMPP</div>
						<div>{subCategoryMetadata.bmppGroupName || '-'}</div>
					</div>
					{/* Row 2 */}
					<div>
						<div style={{ fontWeight: 600 }}>Prospect ID</div>
						<div>{subCategoryMetadata.prospectId || '-'}</div>
					</div>
					<div>
						<div style={{ fontWeight: 600 }}>No. MKL</div>
						<div>{subCategoryMetadata.mklNo || '-'}</div>
					</div>
					{/* Row 3 */}
					<div>
						<div style={{ fontWeight: 600 }}>No. MPP</div>
						<div>{mppMetadata.mppNo || '-'}</div>
					</div>
					<div>
						<div style={{ fontWeight: 600 }}>
							No. Kesepakatan Realisasi Pencairan
						</div>
						<div>{disbursementMetadata.disbursementNo || '-'}</div>
					</div>
				</div>

				{/* Skema Accordion */}
				<Collapse
					defaultActiveKey={['skema']}
					items={[
						{
							key: 'skema',
							label: <strong>Skema</strong>,
							style: { backgroundColor: '#E6F7FF' },
							children: (
								<div>
									Skema Pembiayaan: {disbursementMetadata.scheme || '-'}
								</div>
							),
						},
					]}
				/>

				<div>
					<h3 style={{ marginBottom: 16 }}>List Dokumen</h3>
					{sections.length === 1 ? (
						renderSection(sections[0])
					) : (
						<Collapse
							activeKey={
								keyword ? sections.map((section) => section.id) : activeKey
							}
							onChange={(keys) => setActiveKey(keys as string[])}
							items={sections.map((section) => {
								return {
									key: section.id,
									label: section.label,
									children: renderSection(section),
								};
							})}
						/>
					)}
				</div>
			</div>
		);
	} else if (isFinancingDisbursementDocument) {
		// If no disbursement selected (and not caught by previous conditions), show nothing or default
		// This case should be handled by the "return" blocks above for selection UI
		sections = [];
	} else {
		// Default behavior for other documents
		sections = data.sections.filter((section) => section.documents.length);
	}

	return sections.length === 1 ? (
		renderSection(sections[0])
	) : isFinancingDisbursementWithSelectedDisbursement ? (
		renderFinancingDisbursementView()
	) : (
		<Collapse
			activeKey={keyword ? sections.map((section) => section.id) : activeKey}
			onChange={(keys) => setActiveKey(keys as string[])}
			items={sections.map((section) => {
				return {
					key: section.id,
					label: section.label,
					children: renderSection(section),
				};
			})}
		/>
	);
};

export default MonitoringDocumentDocumentDetail;
